import React, { useState, useEffect, useContext, Fragment } from 'react';
import { SettingsContext } from '../../context/SettingsProvider';

const highlightKeywords = (text, keywords, italicWords) => {
  return text
    .split(' ')
    .map((word, index) => {
      const lowerCaseWord = word.toLowerCase().replace(/[,.;:!?]/g, '');
      if (keywords.includes(lowerCaseWord)) {
        return (
          <span
            key={index}
            className="font-bold text-blue-500 dark:text-indigo-500"
          >
            {word}
          </span>
        );
      }
      if (italicWords.includes(lowerCaseWord)) {
        return (
          <span key={index} className="italic underline underline-offset-2">
            {word}
          </span>
        );
      }
      return word;
    })
    .reduce((accu, elem) => {
      return accu === null ? [elem] : [...accu, ' ', elem];
    }, null);
};

const keywords = [
  'uploaded',
  'uploaded:',
  'added',
  'added:',
  'deleted',
  'deleted:',
  'changed',
  'changed:',
  'modified',
  'modified:',
  'created',
  'created:',
  'issues',
  'issues:',
];
const italicWords = [
  'vendor',
  'vendors',
  'website',
  'websites',
  'file',
  'contact',
  'contacts',
  'files',
  'comment',
  'comments',
  'agent',
];

export default function Notifications() {
  const { getNotificationPreferences, updateNotificationPreferences } =
    useContext(SettingsContext);
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [reverseMapping, setReverseMapping] = useState({});

  const transformSettings = (originalSettings) => {
    const mapping = {
      create: 'A new {category} has been added {type}',
      modify: 'An existing {category} has been modified',
      delete: 'A {category} has been deleted',
      issue: 'Issues found on a {category}',
      statusChange: 'An agent status has changed',
    };

    const pluralToSingular = {
      vendors: 'vendor',
      websites: 'website',
      contacts: 'contact',
    };

    const transformed = {};
    const localReverseMapping = {};
    for (const [category, actions] of Object.entries(originalSettings)) {
      transformed[category] = {};
      if (category === 'comments') {
        transformed[category]['A new comment has been added to a website'] =
          originalSettings[category]?.website?.create || {
            email: false,
            push: false,
          };
        transformed[category]['A new comment has been added to a vendor'] =
          originalSettings[category]?.vendor?.create || {
            email: false,
            push: false,
          };
        localReverseMapping['A new comment has been added to a website'] = {
          action: 'create',
          subCategory: 'website',
        };
        localReverseMapping['A new comment has been added to a vendor'] = {
          action: 'create',
          subCategory: 'vendor',
        };
      } else if (category === 'Files') {
        transformed[category]['A new file has been added to a website'] =
          originalSettings[category]?.website?.create || {
            email: false,
            push: false,
          };
        transformed[category]['A website file has been deleted'] =
          originalSettings[category]?.website?.delete || {
            email: false,
            push: false,
          };
        transformed[category]['A new file has been added to a vendor'] =
          originalSettings[category]?.vendor?.create || {
            email: false,
            push: false,
          };
        transformed[category]['A vendor file has been deleted'] =
          originalSettings[category]?.vendor?.delete || {
            email: false,
            push: false,
          };
        localReverseMapping['A new file has been added to a website'] = {
          action: 'create',
          subCategory: 'website',
        };
        localReverseMapping['A website file has been deleted'] = {
          action: 'delete',
          subCategory: 'website',
        };
        localReverseMapping['A new file has been added to a vendor'] = {
          action: 'create',
          subCategory: 'vendor',
        };
        localReverseMapping['A vendor file has been deleted'] = {
          action: 'delete',
          subCategory: 'vendor',
        };
      } else {
        for (const [action, settings] of Object.entries(actions)) {
          if (action in mapping) {
            const singularCategory =
              pluralToSingular[category.toLowerCase()] ||
              category.toLowerCase();
            const description = mapping[action]
              .replace('{category}', singularCategory)
              .replace('{type}', '');
            transformed[category][description] = settings;
            localReverseMapping[description] = { action, subCategory: null };
          }
        }
      }
    }
    return { transformed, localReverseMapping };
  };

  useEffect(() => {
    getNotificationPreferences()
      .then((fetchedSettings) => {
        if (fetchedSettings) {
          setOriginalSettings(fetchedSettings);
          const { transformed, localReverseMapping } =
            transformSettings(fetchedSettings);
          setSettings(transformed);
          setReverseMapping(localReverseMapping);
        } else {
          console.warn('Fetched settings are empty or undefined.');
        }
      })
      .catch((err) => {
        console.error('Error fetching settings:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSetting = async (mainKey, subKey, type) => {
    const updatedOriginalSettings = JSON.parse(
      JSON.stringify(originalSettings)
    );
    const updatedSettings = JSON.parse(JSON.stringify(settings));
    const originalActionInfo = reverseMapping[subKey];
    if (!originalActionInfo) {
      console.error(`Invalid subKey: ${subKey}`);
      return;
    }
    const { action: originalActionName, subCategory } = originalActionInfo;
    if (['comments', 'Files'].includes(mainKey)) {
      if (updatedOriginalSettings[mainKey][subCategory]) {
        updatedOriginalSettings[mainKey][subCategory][originalActionName][
          type
        ] =
          !updatedOriginalSettings[mainKey][subCategory][originalActionName][
            type
          ];
        updatedSettings[mainKey][subKey][type] =
          !updatedSettings[mainKey][subKey][type];
      } else {
        console.error(`Invalid subCategory: ${subCategory}`);
        return;
      }
    } else {
      if (updatedOriginalSettings[mainKey][originalActionName]) {
        updatedOriginalSettings[mainKey][originalActionName][type] =
          !updatedOriginalSettings[mainKey][originalActionName][type];
        updatedSettings[mainKey][subKey][type] =
          !updatedSettings[mainKey][subKey][type];
      } else {
        console.error(
          `Invalid mainKey or originalActionName: ${mainKey}, ${originalActionName}`
        );
        return;
      }
    }
    try {
      await updateNotificationPreferences(updatedOriginalSettings);
      setOriginalSettings(updatedOriginalSettings);
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Could not update settings:', error);
    }
  };

  return (
    <div id="notifications" className="w-full bg-gray-100 px-4 py-6 dark:bg-stone-800 sm:w-3/4 sm:px-12">
      <div className="max-w-4xl">
        <div className="space-y-8">
          {Object.keys(settings).map((mainKey) => (
            <div
              key={mainKey}
              className="rounded-lg bg-white p-8 shadow-lg dark:bg-stone-900"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200">
                {mainKey.charAt(0).toUpperCase() + mainKey.slice(1)}
              </h3>
              <div className="flex items-center">
                <span className="flex-1 select-none text-lg text-gray-700 dark:text-gray-200"></span>
                <span className="select-none text-lg text-gray-700 dark:text-gray-200">
                  Email
                </span>
                <span className="ml-4 select-none text-lg text-gray-700 dark:text-gray-200">
                  Push
                </span>
              </div>
              {Object.keys(settings[mainKey]).map((subKey) => (
                <Fragment key={subKey}>
                  {settings[mainKey][subKey].email !== undefined ? (
                    <div className="mb-4 mr-3 flex items-center space-x-9">
                      <span className="flex-1 text-lg text-gray-700 dark:text-gray-200">
                        {highlightKeywords(subKey, keywords, italicWords)}
                      </span>
                      <input
                        type="checkbox"
                        id={`${mainKey}-${subKey}-email`}
                        checked={settings[mainKey][subKey].email}
                        onChange={() => toggleSetting(mainKey, subKey, 'email')}
                        className="rounded text-blue-600 focus:ring focus:ring-blue-200"
                      />
                      <input
                        type="checkbox"
                        id={`${mainKey}-${subKey}-push`}
                        checked={settings[mainKey][subKey].push}
                        onChange={() => toggleSetting(mainKey, subKey, 'push')}
                        className="rounded text-blue-600 focus:ring focus:ring-blue-200"
                      />
                    </div>
                  ) : (
                    <div>
                      <h4 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-200">
                        {subKey.charAt(0).toUpperCase() + subKey.slice(1)}
                      </h4>
                      {Object.keys(settings[mainKey][subKey]).map(
                        (nestedSubKey) => (
                          <div
                            key={nestedSubKey}
                            className="mb-4 flex items-center space-x-4"
                          >
                            <span className="flex-1 text-lg text-gray-700 dark:text-gray-200">
                              {highlightKeywords(
                                nestedSubKey,
                                keywords,
                                italicWords
                              )}
                            </span>
                            <input
                              type="checkbox"
                              id={`${mainKey}-${subKey}-${nestedSubKey}-email`}
                              checked={
                                settings[mainKey][subKey][nestedSubKey].email
                              }
                              onChange={() =>
                                toggleSetting(
                                  mainKey,
                                  `${subKey}.${nestedSubKey}`,
                                  'email'
                                )
                              }
                              className="rounded text-blue-600 focus:ring focus:ring-blue-200"
                            />
                            <input
                              type="checkbox"
                              id={`${mainKey}-${subKey}-${nestedSubKey}-push`}
                              checked={
                                settings[mainKey][subKey][nestedSubKey].push
                              }
                              onChange={() =>
                                toggleSetting(
                                  mainKey,
                                  `${subKey}.${nestedSubKey}`,
                                  'push'
                                )
                              }
                              className="rounded text-blue-600 focus:ring focus:ring-blue-200"
                            />
                          </div>
                        )
                      )}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
